import React from 'react';
import Cropper from 'react-cropper';
import {useTranslation} from 'react-i18next';

import ZoomInIcon from '@edna/icons/zoomIn.svg';
import ZoomOutIcon from '@edna/icons/zoomOut.svg';

import {Button} from '../Button';
import {Modal, ModalActions, ModalContent, ModalHeader, showModal} from '../Modals';
import {Icon, Stack} from '../primitives';
import * as S from './style';
import {createImgFromFile, formatImageSize, getImageType, validateCropData} from './utils';

type TProps = {
  minHeight?: number;
  minWidth?: number;
  aspectRatio?: number;
  onClose?: TEmptyFunction;
  onSubmit?: (payload?: File) => void;
  file?: File | null;
  isExactParams?: boolean;
  outputMimeType?: string;
};

const initialSize = {
  width: 0,
  height: 0,
};

const CropControlModal = React.memo<TProps>(
  ({
    aspectRatio,
    onClose,
    onSubmit,
    file = null,
    minWidth = 0,
    minHeight = 0,
    isExactParams = false,
    outputMimeType = file?.type,
  }) => {
    const {t} = useTranslation();
    const cropperRef = React.useRef<HTMLImageElement>(null);
    const cropperInstance = React.useRef<Cropper | null>(null);

    const [size, setSize] = React.useState(initialSize);
    const [naturalSize, setNaturalSize] = React.useState(initialSize);
    const [src, setSrc] = React.useState('');

    React.useEffect(() => {
      if (file) {
        createImgFromFile(file, (_, newSrc) => setSrc(newSrc));
      }
    }, [file]);

    const updateSize = React.useCallback(() => {
      if (!cropperInstance.current) {
        return;
      }

      const {width, height} = cropperInstance.current.getData(true);

      setSize({width, height});
    }, []);

    const getCroppedFile = React.useCallback(async () => {
      if (!cropperRef.current || !file || !cropperInstance.current) {
        return;
      }

      const image = cropperInstance.current.getCroppedCanvas();

      // condition for FireFox
      if (!image) {
        return;
      }

      const croppedFile: File | undefined = await new Promise((resolve) =>
        image.toBlob((blob: Blob | null) => {
          if (blob === null) {
            resolve(undefined);

            return;
          }

          resolve(
            new File([blob], file.name, {
              type: file.type,
            }),
          );
        }, outputMimeType),
      );

      return croppedFile;
    }, [file]);

    const handleSubmit = async () => {
      const croppedFile = await getCroppedFile();

      onSubmit?.(croppedFile);
      onClose?.();
    };

    const handleClose = () => {
      onClose?.();
      onSubmit?.();
    };

    const handleInit = () => {
      if (!cropperInstance.current) {
        return;
      }

      updateSize();

      const {naturalWidth, naturalHeight} = cropperInstance.current.getImageData();

      setNaturalSize({width: Math.round(naturalWidth), height: Math.round(naturalHeight)});
    };

    const handleCrop = React.useCallback(
      (event: Cropper.CropEvent) => {
        if (!cropperRef.current || !cropperInstance.current) {
          return;
        }

        const result = validateCropData({
          width: Number(event.detail.width.toFixed(5)),
          height: Number(event.detail.height.toFixed(5)),
          minWidth,
          minHeight,
          isExactParams,
          cropperInstance: cropperInstance.current,
        });

        if (!result) {
          return;
        }

        cropperInstance.current?.setData({
          width: result.width,
          height: result.height,
        });
      },
      [minHeight, minWidth, isExactParams],
    );

    const handleZoomIn = React.useCallback(() => {
      const zoomedData = cropperInstance.current?.zoom(0.1);

      if (zoomedData && minWidth && minHeight) {
        const {width, height} = zoomedData.getData();

        if (width < minWidth || height < minHeight) {
          cropperInstance.current?.zoom(-0.1);
        }
      }

      updateSize();
    }, [minHeight, minWidth, updateSize]);

    const handleZoomOut = React.useCallback(() => {
      cropperInstance.current?.zoom(-0.1);
      updateSize();
    }, [updateSize]);

    if (!(file instanceof File)) {
      return null;
    }

    return (
      <Modal width="480px" onExit={handleClose}>
        <ModalHeader>{t('EdnaCropControl:title')}</ModalHeader>
        <ModalContent>
          <S.Crop
            onInitialized={(instance) => {
              cropperInstance.current = instance;
            }}
            cropBoxResizable={!isExactParams}
            aspectRatio={aspectRatio}
            src={src}
            center={false}
            dragMode="move"
            viewMode={1}
            autoCropArea={1}
            autoCrop
            zoomOnWheel={false}
            responsive
            crop={handleCrop}
            cropend={updateSize}
            ready={handleInit}
            ref={cropperRef}
          />
          <Stack size="4" direction="ROW">
            <Stack size="2" direction="ROW">
              <Button size="S" square onClick={handleZoomOut}>
                <Icon as={ZoomOutIcon} size="16px" />
              </Button>
              <Button size="S" square onClick={handleZoomIn}>
                <Icon as={ZoomInIcon} size="16px" />
              </Button>
            </Stack>
            <Stack size="1">
              <S.Info>{file.name}</S.Info>
              <S.Info>
                {t('EdnaCropControl:originalSize', {
                  width: naturalSize.width,
                  height: naturalSize.height,
                  type: getImageType(file),
                  size: formatImageSize(file),
                })}
              </S.Info>
              <S.CroppedInfo>
                {t('EdnaCropControl:targetSize', {
                  width: size.width,
                  height: size.height,
                })}
              </S.CroppedInfo>
            </Stack>
          </Stack>
        </ModalContent>
        <ModalActions>
          <S.LeftButton
            onClick={handleClose}
            data-selector="EdnaCropControl:cancel"
            appearance="secondary"
          >
            {t('EdnaCropControl:cancel')}
          </S.LeftButton>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            data-selector="EdnaCropControl:submit"
          >
            {t('EdnaCropControl:submit')}
          </Button>
        </ModalActions>
      </Modal>
    );
  },
);

CropControlModal.displayName = 'CropControlModal';

const showCropControlModal = (options: TProps) =>
  showModal((closeModal) => <CropControlModal onClose={closeModal} {...options} />);

export {CropControlModal, showCropControlModal};
export type {TProps as TCropControlProps};
